import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../Redux/Store';
import { setCompany } from '../../Redux/Company/companysSlice';
import { CompanyState } from '../../types/company';
import { createApiRequest } from '../../utils/apiUtils';
import ErrorMessage from '../../Components/Message/ErrorMessage';
import Header from '../../Components/Header/Header';
import messages from '../../Components/Message/messages';

const Home: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const companyData = useSelector((state: RootState) => state.company);
  const [error, setError] = useState<string | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const clerkId = searchParams.get('storeClerkId');

  // メニュー管理・テーブル管理は管理者のみ。店員(CLERK)には無効表示にする。
  const isManager = localStorage.getItem('role') === 'ROLE_MANAGER';

  useEffect(() => {
    if (!clerkId) {
      setError(messages.errors.noStoreClerkId);
      navigate('/login');
      return;
    }

    // createApiRequest は 401 時のトークンリフレッシュ＋失効時のフォールバック
    // （セッション破棄→/login 遷移）を内包する。
    createApiRequest<CompanyState>(`/clerk/${clerkId}`, {
      method: 'GET',
      requiresAuth: true,
    })
      .then((data) => {
        dispatch(setCompany(data)); // Redux に保存
      })
      .catch((error) => {
        console.error('Error:', error);
        setError(messages.errors.fetchFailed);
      });
  }, [clerkId, navigate, dispatch]);

  const handleNavigation = (storeId: string, path: string, disabled: boolean) => {
    if (!disabled) {
      navigate(`/${storeId}${path}`);
    }
  };

  return (
    <div>      
      <Header />
      <div style={styles.container}>
        {error && (
          <ErrorMessage
            message={error}
            onClose={() => setError(null)}
          />
        )}
        <div style={styles.menuGrid}>
          {companyData.stores.map((store) => (
            <div key={store.id}>
              <h2>管理メニュー</h2>
              <div style={styles.menuGrid}>
                <MenuItem
                  label="店内注文管理"
                  path="/manage/order"
                  storeId={store.id}
                  disabled={false}
                  onClick={handleNavigation}
                />
                <MenuItem
                  label={isManager ? 'メニュー管理' : 'メニュー管理（管理者のみ）'}
                  path="/manage/menu"
                  storeId={store.id}
                  disabled={!isManager}
                  onClick={handleNavigation}
                />
                <MenuItem
                  label={isManager ? 'テーブル管理' : 'テーブル管理（管理者のみ）'}
                  path="/manage/dining-table"
                  storeId={store.id}
                  disabled={!isManager}
                  onClick={handleNavigation}
                />
                <MenuItem
                  label="店舗管理"
                  path="/manage/store"
                  storeId={store.id}
                  disabled={false}
                  onClick={handleNavigation}
                />
                <MenuItem
                  label="予約管理"
                  path="/manage/reservation"
                  storeId={store.id}
                  disabled={false}
                  onClick={handleNavigation}
                />
                <MenuItem
                  label={isManager ? '予約設定' : '予約設定（管理者のみ）'}
                  path="/manage/reservation-settings"
                  storeId={store.id}
                  disabled={!isManager}
                  onClick={handleNavigation}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

type MenuItemProps = {
  label: string;
  path: string;
  storeId: string;
  disabled: boolean;
  onClick: (storeId: string, path: string, disabled: boolean) => void;
};

const MenuItem: React.FC<MenuItemProps> = ({ label, path, storeId, disabled, onClick }) => {
  return (
    <div
      style={{
        ...styles.menuItem,
        ...(disabled ? styles.disabledMenuItem : {}),
      }}
      onClick={() => onClick(storeId, path, disabled)}
    >
      <span>{label}</span>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '20px',
    width: '100%',
    maxWidth: '800px',
  },
  menuItem: {
    backgroundColor: '#F46B4D',
    color: '#fff',
    borderRadius: '10px',
    height: '150px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s',
  },
  disabledMenuItem: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
};

export default Home;